import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import CloseDrawerRight from '../assets/svgs/CloseDrawerRight';
import { useIsCourseStudent } from '../instances/useInstance';
import { useIsMcGrawHill } from '../mcgrawHill/mcgrawHill'; // mcgraw-hill
import McGrawHillGreetingLogo from '../mcgrawHill/McGrawHillGreetingLogo'; // mcgraw-hill
import McGrawHillDemoDots from '../mcgrawHill/McGrawHillDemoDots'; // mcgraw-hill
import { createMcGrawHillScriptedStreamDeps } from '../mcgrawHill/mcgrawHillScriptedStream'; // mcgraw-hill
import { createChatStreamDeps } from '../services/chatStream/chatStreamDeps';
import { Spinner } from '@/components/ui/spinner';
import { useAppContextEnvelope } from '@/chatContext/useAppContextEnvelope';
import { cn } from '@/lib/utils';
import ChatConversationList from '../components/ora/Chat/Companion/ChatConversationList';
import ChatConversationView from '../components/ora/Chat/Companion/ChatConversationView';
import ChatUnavailable from '../components/ora/Chat/Companion/ChatUnavailable';
import ChatDocumentWorkspace from '../components/ora/Chat/Companion/ChatDocumentWorkspace';
import UserAvatarButton from '../components/ora/Global/UserAvatarButton/UserAvatarButton';
import { useChatConversations } from '../components/ora/Chat/Companion/useChatConversations';
import { useChatPageRoute } from '../components/ora/Chat/Companion/useChatPageRoute';
import { useCompanionOpenedCapture } from '../components/ora/Chat/Companion/companionAnalytics';
import {
  useChatDocumentViewer,
  useHostedChatDocument,
} from '../hooks/useChatDocumentViewer';
import { ESCAPE_IGNORE_SELECTOR } from '../utils/escapeIgnoreSelector';
import {
  selectConversation,
  selectConversationMessageIds,
  type ChatRootState,
} from '../slices/chat/chatSelectors';
import { abortChatTurn } from '../slices/chat/sendChatTurn';
import { examsSelectors } from '../slices/exams/examsSlice';
import type { AppDispatch } from '../store/store';
import {
  getStoredChatSidebarCollapsed,
  setStoredChatSidebarCollapsed,
} from '../utils/chatSidebarPreferences';
import { useKnowledgeMapEnabled } from '../utils/knowledgeMapFlag';
import { isWebGL2Supported } from '../utils/webglDetection';
import KnowledgeMapIcon from './knowledgeMap/KnowledgeMapIcon';
import type { KnowledgeMapCloseOptions } from './knowledgeMap/KnowledgeMap';
import { WATER_EMANATION_FROM_BOTTOM } from './knowledgeMap/knowledgeMapFraming';
import { setHistoryRailShown } from './historyRailStore';
import {
  registerKnowledgeMapCloser,
  setKnowledgeMapOpen,
} from './knowledgeMapOpenStore';
import { MAP_QUERY_PARAM } from './landingDecision';
import { typeIntoTextarea } from './typeIntoTextarea';
import { requestWaterRipple } from './waterRipple';

// three.js and the map's scene load only when the map is first opened.
const KnowledgeMap = lazy(() => import('./knowledgeMap/KnowledgeMap'));

// A course student's start screen (Courses V1), fetched only for them.
const CourseStartPanel = lazy(
  () => import('../components/ora/Course/CourseStartPanel')
);

/** Which control opened the knowledge map, to hand focus back to on close. */
type KnowledgeMapEntry = 'empty-state';

const COMPOSER_TEXTAREA_SELECTOR =
  '[data-testid="chat-composer-surface"] textarea';

const TEXT_FIELD_SELECTOR =
  'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], input[type="tel"], input[type="number"], input[type="password"]';

/** A focused text field or editable region the student has typed into. */
const holdsTypedText = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;

  if (
    (target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement) &&
    target.matches(TEXT_FIELD_SELECTOR)
  ) {
    return target.value !== '';
  }

  const editable = target.closest(
    '[contenteditable]:not([contenteditable="false"])'
  );

  return editable !== null && (editable.textContent ?? '') !== '';
};

const inertWhen = (isInert: boolean) =>
  (isInert ? { inert: '' } : {}) as Record<string, string>;

interface OsDesktopProps {
  /**
   * `/` is the current route with no window on top: the desktop owns the URL
   * (conversation deep links, study-set returns) and is the chat surface the
   * student is using. Beneath a window it is only a backdrop.
   */
  isActive: boolean;
  /** A window is open on top; the desktop recedes behind it. */
  isDimmed: boolean;
}

/**
 * The AI desktop beneath every window: the AI Companion's thread and
 * composer over the water, with the conversation history in a rail on the
 * right. It stays mounted while windows come and go so the conversation on
 * it is where the student left it.
 */
const OsDesktop = ({ isActive, isDimmed }: OsDesktopProps) => {
  const isCourseStudent = useIsCourseStudent();
  // mcgraw-hill: the instance's logo leads the greeting in place of the
  // student's avatar (the avatar stays if the instance has no logo).
  const isMcGrawHill = useIsMcGrawHill();

  // mcgraw-hill: the desktop chat answers its scripted demo questions itself
  // (mcgrawHillScriptedAnswers.ts); everything else goes to the server.
  const scriptedStreamDeps = useMemo(
    () =>
      isMcGrawHill
        ? createMcGrawHillScriptedStreamDeps(createChatStreamDeps())
        : undefined,
    [isMcGrawHill]
  );

  const greetingMark = isMcGrawHill ? (
    <McGrawHillGreetingLogo />
  ) : (
    <UserAvatarButton />
  );

  const surfaceRef = useRef<HTMLDivElement>(null);
  // History is fetched once the desktop has actually been used, not on every
  // page load beneath a window (nor at all for a guest on a public route).
  const [hasBeenActive, setHasBeenActive] = useState(isActive);

  useEffect(() => {
    if (isActive) setHasBeenActive(true);
  }, [isActive]);

  const conversations = useChatConversations({
    enabled: hasBeenActive,
    // The desktop owns its route the way /chat did: a bare `/` is New Chat
    // and an id in the URL is selected by the route hook. Generic-thread
    // restoration belongs to the drawer and would otherwise race either.
    threading: false,
    threadAnchor: null,
  });

  useChatPageRoute(conversations, { enabled: isActive });

  // The desktop is the full chat page; it must not offer its envelope as the
  // fallback for a send made from a window's companion.
  const { envelope, isChatAvailable } = useAppContextEnvelope({
    surface: 'chat_page',
    publish: isActive,
  });

  const getEnvelope = useCallback(() => envelope, [envelope]);

  // The desktop is the chat page, so it reports opening whenever no window
  // covers it — which is every app load and every window closed back to it,
  // hence its own trigger, so a funnel can leave these out (ORA-1118).
  useCompanionOpenedCapture({
    isOpen: isActive && isChatAvailable,
    surface: 'chat_page',
    trigger: 'home_landing',
    envelope,
  });

  const activeConversationId = conversations.activeConversationId ?? null;
  const primaryExamId = useSelector(examsSelectors.selectPrimaryExam)?.id;

  const hasThreadMessages = useSelector(
    (state: ChatRootState) =>
      !!activeConversationId &&
      selectConversationMessageIds(state, activeConversationId).length > 0
  );

  // The answer still streaming on the open conversation, if any.
  const streamingTurnId = useSelector(
    (state: ChatRootState) =>
      selectConversation(state, activeConversationId)?.activeTurnId ?? null
  );

  // A document opened from the desktop's thread (ORA-736) takes the left of
  // the desktop, the thread moves right, and the history rail stands aside
  // until it closes — without touching the student's own collapsed choice.
  // Only a document opened here, and only while no window covers the desktop
  // (a window opening closes it; this holds for the render in between).
  const desktopDocument = useHostedChatDocument('desktop');
  const { close: closeDocument } = useChatDocumentViewer();
  const hasDocument = isActive && desktopDocument !== null;
  // The knowledge map (ORA-867) takes the chat column's place while open. It
  // outlasts a window opened over the desktop (dimmed with the rest, and
  // still there when the window closes) and stands aside for a document.
  const isKnowledgeMapEnabled = useKnowledgeMapEnabled();
  const isMapAvailable = isKnowledgeMapEnabled && isWebGL2Supported();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const isMapShown = isMapOpen && isMapAvailable && !hasDocument;
  // The shell stands its floating chrome aside while the map has the screen:
  // only on the active desktop (a window over it brings its own chrome).
  const isMapOnScreen = isMapShown && isActive;

  useEffect(() => {
    setKnowledgeMapOpen(isMapOnScreen);
  }, [isMapOnScreen]);

  useEffect(() => () => setKnowledgeMapOpen(false), []);
  const dispatch = useDispatch<AppDispatch>();
  // Esc on a conversation starts a new one, the keyboard twin of New Chat.
  // An answer still streaming is stopped first: it belongs to the thread
  // being left, and left running it kept the fresh chat's composer on Stop.
  // Anything that claims Esc first keeps it: a dialog, menu or popover open
  // anywhere, a handler that already took the key (read-aloud, the history
  // search, an action card), or a field with text in it. An empty composer
  // does not count as a field in use, since focus rests there. With a side
  // document open, Esc closes the document instead (below).
  const { startNew } = conversations;

  useEffect(() => {
    if (!isActive || !hasThreadMessages || hasDocument || isMapShown) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;

      // Nothing focused puts the key on the window or document, not an
      // element.
      const target = event.target instanceof Element ? event.target : null;

      const field = target?.closest<HTMLElement>(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
      );

      const fieldValue =
        field && 'value' in field
          ? String((field as HTMLInputElement).value)
          : (field?.textContent ?? '');

      if (field && fieldValue.trim() !== '') return;

      if (
        document.querySelector(
          '[role="dialog"], [role="alertdialog"], [role="menu"], [data-radix-popper-content-wrapper]'
        )
      )
        return;

      event.preventDefault();
      if (streamingTurnId) dispatch(abortChatTurn(streamingTurnId));
      startNew();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    dispatch,
    hasDocument,
    hasThreadMessages,
    isActive,
    isMapShown,
    startNew,
    streamingTurnId,
  ]);

  // Dimmed behind a window the desktop is pointer-inert via CSS; `inert`
  // takes its composer and rail out of the tab order and the a11y tree too.
  // (React 18 has no inert prop, hence the imperative set.)
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    surface.inert = isDimmed;
    return () => {
      surface.inert = false;
    };
  }, [isDimmed]);

  // The history rail. Ghosted so the conversation owns the surface — on a
  // thread as much as on the empty desktop — and solid only while hovered or
  // focused. The collapsed choice persists.
  const [isRailCollapsed, setIsRailCollapsed] = useState(
    getStoredChatSidebarCollapsed
  );

  const [isRailHovered, setIsRailHovered] = useState(false);
  const [isRailFocused, setIsRailFocused] = useState(false);
  // A conversation's actions menu: it takes the pointer and focus into its
  // portal, which would otherwise ghost the rail under it while it is open.
  const [isRailMenuOpen, setIsRailMenuOpen] = useState(false);

  useEffect(() => {
    setStoredChatSidebarCollapsed(isRailCollapsed);
  }, [isRailCollapsed]);

  const isRailSolid = isRailHovered || isRailFocused || isRailMenuOpen;
  // Where focus goes back to when the document closes: the control that
  // opened it, read as the document opens (before the viewer can take focus).
  const documentOpenerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!hasDocument) return;

    const opener = document.activeElement;

    documentOpenerRef.current =
      opener instanceof HTMLElement && opener !== document.body ? opener : null;
  }, [hasDocument]);

  // Closing the document removes the viewer, and focus with it if it was in
  // there (its close control, or its Escape). Hand it back to the opener if
  // that is still on screen, else to the composer.
  const hadDocument = useRef(hasDocument);

  useEffect(() => {
    const wasOpen = hadDocument.current;
    hadDocument.current = hasDocument;
    if (!wasOpen || hasDocument || !isActive) return;

    const opener = documentOpenerRef.current;
    documentOpenerRef.current = null;
    const focused = document.activeElement;

    if (focused && focused !== document.body && focused.isConnected) return;

    if (opener?.isConnected && !opener.closest('[inert]')) {
      opener.focus();
      return;
    }

    surfaceRef.current
      ?.querySelector<HTMLTextAreaElement>(COMPOSER_TEXTAREA_SELECTOR)
      ?.focus();
  }, [hasDocument, isActive]);

  // Escape closes the desktop's document, unless something else owns the
  // key: an open dialog, menu or popover; a field holding the student's
  // unsent text; or the viewer itself (it closes on its own Escape and marks
  // the event handled). No window can be open over the desktop meanwhile:
  // `hasDocument` requires the desktop to be the active surface.
  useEffect(() => {
    if (!hasDocument) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector(ESCAPE_IGNORE_SELECTOR)) return;
      if (holdsTypedText(event.target)) return;

      event.preventDefault();
      closeDocument();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeDocument, hasDocument]);

  const mapOpenerRef = useRef<KnowledgeMapEntry | null>(null);
  const pendingMapCloseRef = useRef<KnowledgeMapCloseOptions | null>(null);

  // Opening the map ripples the water from the point the cloud rises out of.
  const openMap = (entry: KnowledgeMapEntry) => {
    mapOpenerRef.current = entry;
    setIsMapOpen(true);

    requestWaterRipple({
      x: 0.5,
      y: WATER_EMANATION_FROM_BOTTOM,
      strength: 0.045,
    });
  };

  // `/?map=1` (or just `?map`) opens the desktop straight into the map, for
  // links from demos and admin tools: on a cold load, or a link followed while
  // the desktop is already mounted beneath a window. Only at `/`: the desktop
  // stays mounted under every window route, whose query is not its own. The
  // ask is remembered until it can be met, since the map's gate (admin or
  // demo account, WebGL2) and the desktop becoming active may settle a render
  // later, and is forgotten if the student leaves `/` first. It is removed
  // from the URL once met, so refreshing or going back does not reopen the
  // map.
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAtDesktop = pathname === '/';
  const hasMapAskInUrl = isAtDesktop && searchParams.has(MAP_QUERY_PARAM);
  const isMapAskPendingRef = useRef(hasMapAskInUrl);

  useEffect(() => {
    if (!isAtDesktop) {
      isMapAskPendingRef.current = false;
      return;
    }

    if (hasMapAskInUrl) isMapAskPendingRef.current = true;
    if (!isMapAskPendingRef.current || !isActive || !isMapAvailable) return;
    isMapAskPendingRef.current = false;
    openMap('empty-state');

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(MAP_QUERY_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [
    hasMapAskInUrl,
    isActive,
    isAtDesktop,
    isCourseStudent,
    isMapAvailable,
    setSearchParams,
  ]);

  const closeMap = useCallback((options: KnowledgeMapCloseOptions) => {
    pendingMapCloseRef.current = options;
    setIsMapOpen(false);
  }, []);

  // The nav's Home item closes the map from outside the desktop.
  useEffect(
    () => registerKnowledgeMapCloser(() => closeMap({ focus: 'opener' })),
    [closeMap]
  );

  // Closing the map brings the chat column back (its controls are new
  // elements). Focus goes to the control that opened the map if it is on
  // screen again, else (or for Ask Ora) to the composer. Ask Ora's query is
  // typed into the composer, so the chat stays exactly as it was otherwise
  // (a staged prefill would hide the suggested prompts).
  useEffect(() => {
    const pending = pendingMapCloseRef.current;
    if (isMapOpen || !pending) return;
    pendingMapCloseRef.current = null;
    const { focus, composerText } = pending;
    const surface = surfaceRef.current;

    if (composerText !== undefined) {
      const composer = surface?.querySelector<HTMLTextAreaElement>(
        COMPOSER_TEXTAREA_SELECTOR
      );

      if (composer) typeIntoTextarea(composer, composerText);
      return;
    }

    const opener =
      focus === 'opener' && mapOpenerRef.current
        ? surface?.querySelector<HTMLElement>(
            `[data-knowledge-map-entry="${mapOpenerRef.current}"]`
          )
        : null;

    if (opener && !opener.closest('[inert]')) {
      opener.focus();
      return;
    }

    surface
      ?.querySelector<HTMLTextAreaElement>(COMPOSER_TEXTAREA_SELECTOR)
      ?.focus();
  }, [isMapOpen]);

  // Escape closes the map, and does nothing else meanwhile (the new-chat
  // Escape above stands down), unless an open dialog, menu, popover or modal
  // drawer, or a field holding text, owns the key.
  useEffect(() => {
    if (!isMapShown || !isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector(ESCAPE_IGNORE_SELECTOR)) return;
      // A drawer or sheet open over the desktop owns its Escape too.
      if (document.querySelector('[aria-modal="true"]')) return;
      if (holdsTypedText(event.target)) return;

      event.preventDefault();
      closeMap({ focus: 'opener' });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeMap, isActive, isMapShown]);

  // No history, no rail: a student with no chats yet gets the greeting and
  // composer alone. A failed load still shows the rail, with its message.
  const hasConversations =
    conversations.conversations.length > 0 ||
    conversations.isLoading ||
    conversations.error !== null;

  const isRailShown =
    hasConversations && !isRailCollapsed && !hasDocument && !isMapShown;

  // Collapsed, the rail slides off screen; it must leave the tab order too.
  const isRailHidden = isRailCollapsed || hasDocument || isMapShown;

  // The shell stands its streak pill aside while the rail is on screen: the
  // rail runs down to the pill's corner. The desktop says whether the rail
  // belongs on its bare surface (history, or no list read yet), not whether
  // it shows this frame: a window over the desktop, the map or a document
  // hide the rail and the shell's pill alike, so the flag holds through them
  // and is right the moment the desktop is the surface again. Set before
  // paint, so the pill never shows for a frame over the rail.
  const isRailExpected =
    !isCourseStudent &&
    !isRailCollapsed &&
    (hasConversations || !conversations.hasLoaded);

  useLayoutEffect(() => {
    setHistoryRailShown(isRailExpected);
  }, [isRailExpected]);

  useEffect(() => () => setHistoryRailShown(false), []);

  const handleRailBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsRailFocused(false);
    }
  };

  return (
    <div
      className={cn(
        'os-desktop',
        isDimmed && 'os-desktop--dimmed',
        hasDocument && 'os-desktop--document'
      )}
      ref={surfaceRef}
    >
      <section
        aria-label={isMapShown ? 'Knowledge map' : 'Ora chat'}
        className={cn(
          'os-desktop__chat',
          !isRailShown && 'os-desktop__chat--rail-collapsed',
          hasDocument && 'os-desktop__chat--document',
          isMapShown && 'os-desktop__chat--map'
        )}
      >
        {/* The chat stays mounted (and laid out, so its scroll holds) under
            the map: invisible and inert, it keeps an unsent draft, pending
            attachments and the thread where the student left them. The
            wrapper adds no box of its own. */}
        <div
          aria-hidden={isMapShown ? true : undefined}
          className={cn('contents', isMapShown && 'invisible')}
          data-testid="os-desktop-chat-content"
          {...inertWhen(isMapShown)}
        >
          {isCourseStudent ? (
            // A course student's desktop is their start screen (Courses V1):
            // the date, the ring and the module, centred on the water scene.
            // The companion stays in its drawer.
            <div
              className="flex h-full min-h-0 w-full items-center justify-center"
              data-testid="os-desktop-course-start"
            >
              <Suspense fallback={null}>
                <CourseStartPanel isActive={isActive} />
              </Suspense>
            </div>
          ) : isChatAvailable ? (
            // The same element whether a document is open or not, so the
            // thread, its scroll and an unsent draft survive either way.
            <ChatDocumentWorkspace surface="chat_page" isActive={isActive}>
              <ChatConversationView
                surface="chat_page"
                getEnvelope={getEnvelope}
                // mcgraw-hill: scripted demo answers on the bare desktop.
                streamDeps={scriptedStreamDeps}
                // An empty desktop offers the preset chips (ORA-509) as three
                // side-by-side lists under the greeting, with the student's
                // avatar above it; the view hides the chips itself behind anchored
                // content or an initial input. A course student gets none: the
                // presets name medical study the course does not offer.
                showSuggestedPrompts={!isCourseStudent}
                promptLayout="columns"
                emptyStateLead={
                  isMapAvailable && !isMcGrawHill ? (
                    // The map's button sits beside the avatar without pulling
                    // it off the greeting's centre line.
                    <div className="relative mb-4">
                      {greetingMark}
                      <button
                        aria-label="Knowledge map"
                        className="os-desktop__map-entry absolute top-1/2 left-full ml-3 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border-subtle bg-surface-raised/60 text-fg-secondary backdrop-blur-xl transition-colors hover:bg-surface-highlight hover:text-fg-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                        data-knowledge-map-entry="empty-state"
                        onClick={() => openMap('empty-state')}
                        title="Knowledge map"
                        type="button"
                      >
                        <KnowledgeMapIcon />
                      </button>
                    </div>
                  ) : (
                    <div className="mb-4">{greetingMark}</div>
                  )
                }

                onReloadConversation={conversations.reload}
                isThreadResolving={conversations.isResolvingThread}
                isThreadReady={conversations.isThreadReady}
                forceNewConversation={conversations.shouldStartNewThread}
              />
            </ChatDocumentWorkspace>
          ) : (
            <ChatUnavailable />
          )}
        </div>
        {isMapShown ? (
          <Suspense
            fallback={
              <div
                className="fixed inset-0 flex items-center justify-center gap-2 text-sm text-fg-secondary"
                role="status"
              >
                <Spinner size={16} />
                Loading your knowledge map…
              </div>
            }
          >
            {/* Keyed by exam: another exam may have another map (NCLEX:
                nursing, ORA-1086), so the selection, filter and tour start
                over rather than point into the new map's topics. */}
            <KnowledgeMap
              isActive={isActive}
              key={primaryExamId ?? 'no-exam'}
              onClose={closeMap}
            />
          </Suspense>
        ) : null}
      </section>

      {hasConversations &&
      !isCourseStudent &&
      isRailCollapsed &&
      !hasDocument &&
      !isMapShown ? (
        <button
          aria-label="Show chat history"
          className="os-desktop__chat-rail-toggle absolute z-20 flex h-8 w-8 items-center justify-center rounded-full border border-border-subtle bg-surface-raised/60 text-fg-secondary backdrop-blur-xl transition-colors hover:bg-surface-highlight hover:text-fg-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]"
          onClick={() => setIsRailCollapsed(false)}
          title="Show chat history"
          type="button"
        >
          <CloseDrawerRight />
        </button>
      ) : null}

      {hasConversations && !isCourseStudent ? (
        <aside
          aria-label="Chat history"
          className={cn(
            'os-desktop__chat-rail absolute z-20 flex w-60 flex-col overflow-hidden p-3',
            isRailSolid && 'os-desktop__chat-rail--solid',
            isRailHidden && 'os-desktop__chat-rail--collapsed'
          )}
          {...inertWhen(isRailHidden)}
          onBlur={handleRailBlur}
          // Keyboard focus only, re-read on every focus change inside the rail:
          // a click on a history item leaves focus in the rail, and that must
          // not hold it solid after the pointer has gone — even when the click
          // followed typing in the search field, which had made focus visible.
          onFocus={(event) => {
            setIsRailFocused(event.target.matches(':focus-visible'));
          }}
          onMouseEnter={() => setIsRailHovered(true)}
          onMouseLeave={() => setIsRailHovered(false)}
        >
          <div className="flex items-center justify-between gap-2 pb-2">
            <h2 className="m-0! font-heading text-sm! font-medium! text-fg-primary">
              Chats
            </h2>
            <button
              aria-label="Hide chat history"
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-fg-secondary transition-all hover:bg-surface-highlight hover:text-fg-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]',
                isRailSolid ? 'opacity-100' : 'opacity-0'
              )}
              onClick={() => setIsRailCollapsed(true)}
              tabIndex={isRailSolid ? 0 : -1}
              title="Hide chat history"
              type="button"
            >
              <CloseDrawerRight />
            </button>
          </div>
          <ChatConversationList
            conversations={conversations}
            onMenuOpenChange={setIsRailMenuOpen}
            // A New Chat only once there is a chat to leave.
            showNewChat={hasThreadMessages}
            className={cn(
              'min-h-0 flex-1 transition-opacity duration-300',
              isRailSolid ? 'opacity-100' : 'opacity-40'
            )}
          />
        </aside>
      ) : null}
      {/* mcgraw-hill: the presenter's demo dots. */}
      {isMcGrawHill && !hasDocument && !isMapShown ? (
        <McGrawHillDemoDots />
      ) : null}
    </div>
  );
};

export default OsDesktop;
